import { useState } from 'react';
import { LlmSpend } from '../components/LlmSpend';
import { Card, Empty, EntityChip, StatTile } from '../components/ui';
import { ago } from '../lib/format';
import { useApi } from '../lib/useApi';

interface LlmData {
  configs: Array<{
    id: string;
    provider: string;
    modelId: string;
    region: string | null;
    endpoint: string | null;
    purpose: string | null;
    sourceKey: string;
    lastSeenAt: string;
    deployment: {
      name: string;
      product: { key: string; name: string };
      /** Null for shared `-common` infrastructure. */
      client: { id: string; code: string; name: string } | null;
      server: { hostname: string };
    };
  }>;
  byProvider: Record<string, number>;
  usageAvailable: boolean;
  usageNote: string;
}

const PROVIDER_LABEL: Record<string, string> = {
  ANTHROPIC: 'Claude Platform API',
  BEDROCK: 'AWS Bedrock',
  VERTEX: 'Google Vertex AI',
  AZURE: 'Azure',
  OLLAMA: 'Ollama (self-hosted)',
  OTHER: 'Other',
};

/** Short forms for the filter chips, where the full names do not fit a row. */
const PROVIDER_SHORT: Record<string, string> = {
  ANTHROPIC: 'ANT Claude',
  BEDROCK: 'AWS Bedrock',
  VERTEX: 'GCP Gemini',
  AZURE: 'Azure',
  OLLAMA: 'Ollama',
  OTHER: 'Other',
};

export function Llm() {
  const { data, error, loading } = useApi<LlmData>('/llm');
  const [only, setOnly] = useState<string | null>(null);

  if (loading && !data) return <p className="empty">Loading LLM configuration…</p>;
  if (error && !data) return <p className="form-error">{error}</p>;
  if (!data) return null;

  // Built from the providers actually configured, never a fixed list — Bedrock
  // was running on two hosts for weeks while nothing here knew about it, and a
  // hard-coded set would have hidden it a second time.
  const providers = [...new Set(data.configs.map((c) => c.provider))].sort(
    (a, b) => (PROVIDER_SHORT[a] ?? a).localeCompare(PROVIDER_SHORT[b] ?? b),
  );
  const configs = only ? data.configs.filter((c) => c.provider === only) : data.configs;

  return (
    <>
      <div className="page-head">
        <h1>LLM</h1>
      </div>

      {/* Spend leads. Configuration answers "what is wired up", which matters
          less often than "what is this costing" — and the page was previously
          only the former. */}
      <LlmSpend />

      <h2 className="section-head">Configuration</h2>

      <div className="tiles">
        {Object.entries(data.byProvider).map(([p, n]) => (
          <StatTile key={p} label={PROVIDER_LABEL[p] ?? p} value={n} sub="deployments" />
        ))}
        {Object.keys(data.byProvider).length === 0 && (
          <StatTile label="Providers" value={0} sub="nothing discovered yet" />
        )}
      </div>

      {/* Usage and cost are absent, and the page says so in words. The tempting
          alternative — an empty chart with a zero axis — would read as "no spend",
          which is a different and false claim. */}
      {!data.usageAvailable && (
        <Card title="Usage and cost">
          <p className="notice">{data.usageNote}</p>
        </Card>
      )}

      <Card
        title="What each deployment is configured to call"
        actions={
          only && (
            <span className="muted small">
              {configs.length} of {data.configs.length} shown
            </span>
          )
        }
      >
        {/* Filters the configuration list only. The spend section above is keyed
            on provider accounts rather than on these rows, and narrowing a total
            to match a filter set a moment ago is how a number gets quoted out of
            context. */}
        <div className="cred-tabs product-filters">
          <button type="button" className={only === null ? 'tab tab-on' : 'tab'}
            aria-pressed={only === null} onClick={() => setOnly(null)}>
            All <span className="muted">· {data.configs.length}</span>
          </button>
          {providers.map((p) => (
            <button
              key={p}
              type="button"
              className={only === p ? 'tab tab-on' : 'tab'}
              aria-pressed={only === p}
              // Clicking the active filter clears it, so there is always a way
              // back without hunting for "All".
              onClick={() => setOnly(only === p ? null : p)}
              title={PROVIDER_LABEL[p] ?? p}
            >
              {PROVIDER_SHORT[p] ?? p}
              <span className="muted"> · {data.configs.filter((c) => c.provider === p).length}</span>
            </button>
          ))}
        </div>

        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Provider</th><th>Model</th><th>Purpose</th><th>Region</th>
                <th>Product</th><th>Client</th><th>Server</th><th>From</th><th>Seen</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((c) => (
                <tr key={c.id}>
                  <td><EntityChip type="llm" label={PROVIDER_LABEL[c.provider] ?? c.provider} /></td>
                  <td className="mono">{c.modelId === 'unknown' ? <span className="muted">unknown</span> : c.modelId}</td>
                  <td>{c.purpose ?? <span className="muted">—</span>}</td>
                  <td>{c.region ?? <span className="muted">—</span>}</td>
                  <td><EntityChip type="product" label={c.deployment.product.name} /></td>
                  <td>{c.deployment.client?.code ?? <span className="tag tag-common">COMMON</span>}</td>
                  <td className="mono muted small">{c.deployment.server.hostname}</td>
                  {/* The env var each setting came from, so a disagreement is
                      traceable to a line in a file rather than argued about. */}
                  <td className="mono muted small">{c.sourceKey}</td>
                  <td title={c.lastSeenAt}>{ago(c.lastSeenAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Two different empty states. "Nothing matches this filter" and
            "nothing has been discovered at all" look identical on screen and
            mean opposite things — one is a click away from fixing. */}
        {configs.length === 0 && data.configs.length > 0 && (
          <Empty>Nothing configured for {PROVIDER_LABEL[only ?? ''] ?? only}.</Empty>
        )}
        {data.configs.length === 0 && (
          <Empty>
            No LLM configuration discovered yet. It is read from each container&apos;s own
            environment on collection.
          </Empty>
        )}
      </Card>

      <p className="muted small">
        Read from each container&apos;s environment. API keys are never read, stored or displayed —
        only the provider, model, region and endpoint settings are kept.
      </p>
    </>
  );
}
